import React, { useState, useEffect, useCallback } from 'react';
import QuoteGroupCard from './QuoteGroupCard';

/**
 * Kanban column component with compact header
 */
function KanbanColumn({ title, quoteGroups, colorClass = 'bg-indigo-600', skuCount }) {
  return (
    <div className="bg-white rounded-lg overflow-hidden flex flex-col h-full">
      {/* Compact column header */}
      <div className={`px-2 py-1 ${colorClass}`}>
        <div className="flex justify-between items-center">
          <span className="font-medium text-gray-900 text-xs">{title}</span>
          <span className="text-xs text-gray-900/70">{skuCount}</span>
        </div>
      </div>

      {/* Cards container */}
      <div className="flex-1 p-1.5 space-y-1.5 overflow-y-auto">
        {quoteGroups.length === 0 ? (
          <div className="text-center py-4 text-gray-600 text-xs">
            Empty
          </div>
        ) : (
          quoteGroups.map(group => (
            <QuoteGroupCard key={`${group.quoteNumber}-${group.skuClass}`} group={group} />
          ))
        )}
      </div>
    </div>
  );
}

/**
 * Vertical section label on the left side
 */
function SectionLabel({ label, colorClass, totalSkus }) {
  return (
    <div className={`${colorClass} flex items-center justify-center rounded-l-lg`} style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
      <div className="transform rotate-180 py-2 px-1 text-gray-900 font-semibold text-sm tracking-wider">
        <span>{label}</span>
        <span className="text-gray-900/60 ml-1">({totalSkus})</span>
      </div>
    </div>
  );
}

/**
 * Main Production Schedule Board component
 * Displays SKUs organized by 2D/3D workflow sections
 */
export default function ProductionScheduleBoard({ onBackToApp }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sections, setSections] = useState({
    '2d': { PENDING: [], PROD_ART: [], PRINTING: [], ASSEMBLY: [], SHIPPING: [] },
    '3d': { PENDING: [], ACTIVE: [], SHIPPING: [] }
  });
  const [stats, setStats] = useState({
    '2d': { PENDING: 0, PROD_ART: 0, PRINTING: 0, ASSEMBLY: 0, SHIPPING: 0 },
    '3d': { PENDING: 0, ACTIVE: 0, SHIPPING: 0 }
  });
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchSchedule = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = sessionStorage.getItem('msal.backendToken');
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch('/api/job-stages/grouped', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch job stages: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.sections) {
        setSections(data.sections);
      }
      if (data.stats) {
        setStats(data.stats);
      }
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching schedule:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  // Calculate totals
  const total2D = Object.values(stats['2d']).reduce((sum, n) => sum + n, 0);
  const total3D = Object.values(stats['3d']).reduce((sum, n) => sum + n, 0);

  return (
    <div className="flex-1 overflow-auto bg-slate-100 text-gray-900 flex flex-col">

      {/* Error State */}
      {error && (
        <div className="px-4 py-2 shrink-0">
          <div className="bg-red-100 border border-red-500 rounded p-2 text-red-200 text-sm">
            {error}
            <button onClick={fetchSchedule} className="ml-2 underline">Retry</button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && !error && (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-500 border-t-transparent"></div>
        </div>
      )}

      {/* Main Content - Two sections stacked */}
      {!loading && (
        <div className="flex-1 flex flex-col p-2 gap-2 min-h-0">
          {/* 2D Section */}
          <div className="flex-1 flex gap-1 min-h-0">
            {/* Left side label */}
            <SectionLabel label="2D" colorClass="bg-blue-600" totalSkus={total2D} />

            {/* 2D Kanban Columns */}
            <div className="flex-1 grid grid-cols-5 gap-1.5">
              <KanbanColumn
                title="Pending"
                quoteGroups={sections['2d'].PENDING || []}
                colorClass="bg-gray-600"
                skuCount={stats['2d'].PENDING}
              />
              <KanbanColumn
                title="Prod Art"
                quoteGroups={sections['2d'].PROD_ART || []}
                colorClass="bg-blue-600"
                skuCount={stats['2d'].PROD_ART}
              />
              <KanbanColumn
                title="Printing"
                quoteGroups={sections['2d'].PRINTING || []}
                colorClass="bg-cyan-600"
                skuCount={stats['2d'].PRINTING}
              />
              <KanbanColumn
                title="Assembly"
                quoteGroups={sections['2d'].ASSEMBLY || []}
                colorClass="bg-purple-600"
                skuCount={stats['2d'].ASSEMBLY}
              />
              <KanbanColumn
                title="Shipping"
                quoteGroups={sections['2d'].SHIPPING || []}
                colorClass="bg-green-600"
                skuCount={stats['2d'].SHIPPING}
              />
            </div>
          </div>

          {/* 3D Section */}
          <div className="flex gap-1" style={{ height: '35%' }}>
            {/* Left side label */}
            <SectionLabel label="3D" colorClass="bg-orange-600" totalSkus={total3D} />

            {/* 3D Kanban Columns */}
            <div className="flex-1 grid grid-cols-3 gap-1.5">
              <KanbanColumn
                title="Pending"
                quoteGroups={sections['3d'].PENDING || []}
                colorClass="bg-gray-600"
                skuCount={stats['3d'].PENDING}
              />
              <KanbanColumn
                title="Active"
                quoteGroups={sections['3d'].ACTIVE || []}
                colorClass="bg-orange-600"
                skuCount={stats['3d'].ACTIVE}
              />
              <KanbanColumn
                title="Shipping"
                quoteGroups={sections['3d'].SHIPPING || []}
                colorClass="bg-green-600"
                skuCount={stats['3d'].SHIPPING}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
